/**
 * Auth is the ONLY client state that lives outside TanStack Query (ARCHITECTURE.md §2).
 *
 * Tokens are mirrored into `localStorage` so a page reload keeps the session, and every single
 * storage access goes through `safeStorage` — Safari private mode and "block all cookies" throw
 * on `localStorage.getItem`, and that must never take the app down.
 *
 * This store registers itself as the auth bridge for `lib/api.ts`, which is how the axios
 * interceptors read the live access token and how a failed refresh hard-logs-out the app.
 */

import { create } from 'zustand';

import {
  ApiError,
  http,
  registerAuthBridge,
  safeStorage,
  STORAGE_KEYS,
} from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import type {
  AuthResponse,
  ChangePasswordRequest,
  GoogleAuthRequest,
  LoginRequest,
  RegisterRequest,
  UpdateMeRequest,
  User,
} from '@/types/api';

/* -------------------------------------------------------------------------- */
/* Persistence helpers                                                        */
/* -------------------------------------------------------------------------- */

function readUser(): User | null {
  const raw = safeStorage.get(STORAGE_KEYS.user);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null && 'id' in parsed && 'email' in parsed) {
      return parsed as User;
    }
    return null;
  } catch {
    return null;
  }
}

function persistTokens(accessToken: string | null, refreshToken: string | null): void {
  if (accessToken) safeStorage.set(STORAGE_KEYS.accessToken, accessToken);
  else safeStorage.remove(STORAGE_KEYS.accessToken);

  if (refreshToken) safeStorage.set(STORAGE_KEYS.refreshToken, refreshToken);
  else safeStorage.remove(STORAGE_KEYS.refreshToken);
}

function persistUser(user: User | null): void {
  if (!user) {
    safeStorage.remove(STORAGE_KEYS.user);
    return;
  }
  try {
    safeStorage.set(STORAGE_KEYS.user, JSON.stringify(user));
  } catch {
    /* non-serialisable user is impossible, but never let this throw */
  }
}

/* -------------------------------------------------------------------------- */
/* Store                                                                      */
/* -------------------------------------------------------------------------- */

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  /** True until `bootstrap()` has settled. Guards must wait instead of bouncing to /login. */
  isBootstrapping: boolean;

  /** Store a fresh `AuthResponse` (login, register, silent refresh). */
  setTokens: (auth: AuthResponse) => void;
  setUser: (user: User) => void;
  login: (credentials: LoginRequest) => Promise<User>;
  register: (payload: RegisterRequest) => Promise<User>;
  /** `POST /auth/google` — signs in, or signs up, with Google's ID token (docs/GOOGLE_SIGN_IN.md). */
  loginWithGoogle: (payload: GoogleAuthRequest) => Promise<User>;
  /** Best-effort server-side revoke, then wipe local state regardless of the result. */
  logout: () => Promise<void>;
  /** Restore tokens from storage and re-fetch `/auth/me`. Clears everything on failure. */
  bootstrap: () => Promise<void>;
  /** Update the cached user after `PATCH /auth/me`. */
  updateProfile: (patch: UpdateMeRequest) => Promise<User>;
  changePassword: (payload: ChangePasswordRequest) => Promise<void>;
  /** Local-only wipe. Does not call the API. */
  clear: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: readUser(),
  accessToken: safeStorage.get(STORAGE_KEYS.accessToken),
  refreshToken: safeStorage.get(STORAGE_KEYS.refreshToken),
  isAuthenticated: safeStorage.get(STORAGE_KEYS.accessToken) !== null,
  isBootstrapping: true,

  setTokens: (auth) => {
    persistTokens(auth.access_token, auth.refresh_token);
    persistUser(auth.user);
    set({
      user: auth.user,
      accessToken: auth.access_token,
      refreshToken: auth.refresh_token,
      isAuthenticated: true,
      isBootstrapping: false,
    });
  },

  setUser: (user) => {
    persistUser(user);
    set({ user });
  },

  login: async (credentials) => {
    const auth = await http.post<AuthResponse>('/auth/login', credentials, {
      skipAuthRefresh: true,
    });
    get().setTokens(auth);
    return auth.user;
  },

  register: async (payload) => {
    const auth = await http.post<AuthResponse>('/auth/register', payload, {
      skipAuthRefresh: true,
    });
    get().setTokens(auth);
    return auth.user;
  },

  loginWithGoogle: async (payload) => {
    const auth = await http.post<AuthResponse>('/auth/google', payload, {
      skipAuthRefresh: true,
    });
    get().setTokens(auth);
    return auth.user;
  },

  logout: async () => {
    const refreshToken = get().refreshToken;
    if (refreshToken) {
      try {
        await http.post<void>('/auth/logout', { refresh_token: refreshToken });
      } catch {
        /* the session is going away locally either way */
      }
    }
    get().clear();
  },

  bootstrap: async () => {
    const accessToken = safeStorage.get(STORAGE_KEYS.accessToken);
    const refreshToken = safeStorage.get(STORAGE_KEYS.refreshToken);

    if (!accessToken && !refreshToken) {
      set({
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isBootstrapping: false,
      });
      return;
    }

    // Make the tokens visible to the interceptor before the first request goes out.
    set({ accessToken, refreshToken, isAuthenticated: accessToken !== null });

    try {
      // A 401 here triggers the interceptor's single silent refresh; if that also fails the
      // interceptor calls `onAuthFailure` and this request rejects.
      const user = await http.get<User>('/auth/me');
      persistUser(user);
      set({ user, isAuthenticated: true, isBootstrapping: false });
    } catch (error) {
      if (ApiError.is(error) && error.isNetworkError) {
        // Backend unreachable — keep the session and let the UI show an offline state rather
        // than logging a user out over a flaky connection.
        set({ isBootstrapping: false });
        return;
      }
      get().clear();
      set({ isBootstrapping: false });
    }
  },

  updateProfile: async (patch) => {
    const user = await http.patch<User>('/auth/me', patch);
    persistUser(user);
    set({ user });
    return user;
  },

  changePassword: async (payload) => {
    // The server replaces this device's pair either way, so the tab stays signed in.
    const auth = await http.post<AuthResponse>('/auth/change-password', {
      ...payload,
      refresh_token: get().refreshToken ?? undefined,
    });
    get().setTokens(auth);
  },

  clear: () => {
    persistTokens(null, null);
    persistUser(null);
    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isBootstrapping: false,
    });
    // Nothing in the cache belongs to an anonymous visitor.
    queryClient.clear();
  },
}));

/* -------------------------------------------------------------------------- */
/* Bridge into the axios interceptors                                         */
/* -------------------------------------------------------------------------- */

registerAuthBridge({
  getAccessToken: () => useAuthStore.getState().accessToken,
  getRefreshToken: () => useAuthStore.getState().refreshToken,
  onRefreshed: (auth) => {
    useAuthStore.getState().setTokens(auth);
  },
  onAuthFailure: () => {
    useAuthStore.getState().clear();
  },
  syncFromStorage,
});

/* -------------------------------------------------------------------------- */
/* Cross-tab sync                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Take over what another tab wrote to shared storage: a silent refresh, a sign-in, a sign-out.
 * Each tab holds its own copy of the tokens, and a copy left behind is a refresh token that is
 * already spent — sending it is what the server reads as a stolen token.
 */
function syncFromStorage(): void {
  const accessToken = safeStorage.get(STORAGE_KEYS.accessToken);
  const refreshToken = safeStorage.get(STORAGE_KEYS.refreshToken);
  const state = useAuthStore.getState();
  const user = readUser();
  if (
    accessToken === state.accessToken &&
    refreshToken === state.refreshToken &&
    user?.id === state.user?.id
  ) {
    return;
  }

  if (accessToken === null && refreshToken === null) {
    state.clear();
    return;
  }
  // Another account signed in over there: nothing cached here belongs to it.
  if (user !== null && user.id !== state.user?.id) queryClient.clear();
  useAuthStore.setState({
    user: user ?? state.user,
    accessToken,
    refreshToken,
    isAuthenticated: accessToken !== null,
  });
}

const SYNCED_KEYS: ReadonlySet<string | null> = new Set([
  STORAGE_KEYS.accessToken,
  STORAGE_KEYS.refreshToken,
  STORAGE_KEYS.user,
  null, // the other tab cleared all of storage
]);

if (typeof window !== 'undefined') {
  // Fires only for writes made by OTHER tabs of this origin.
  window.addEventListener('storage', (event) => {
    if (SYNCED_KEYS.has(event.key)) syncFromStorage();
  });
}

/* -------------------------------------------------------------------------- */
/* Selector hooks                                                             */
/* -------------------------------------------------------------------------- */

export interface UseAuthResult {
  user: User | null;
  isAuthenticated: boolean;
  isBootstrapping: boolean;
  login: AuthState['login'];
  register: AuthState['register'];
  logout: AuthState['logout'];
  updateProfile: AuthState['updateProfile'];
  changePassword: AuthState['changePassword'];
}

/**
 * The hook components should use. Each field is selected individually so a component only
 * re-renders when the slice it reads actually changes.
 */
export function useAuth(): UseAuthResult {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isBootstrapping = useAuthStore((state) => state.isBootstrapping);
  const login = useAuthStore((state) => state.login);
  const register = useAuthStore((state) => state.register);
  const logout = useAuthStore((state) => state.logout);
  const updateProfile = useAuthStore((state) => state.updateProfile);
  const changePassword = useAuthStore((state) => state.changePassword);

  return { user, isAuthenticated, isBootstrapping, login, register, logout, updateProfile, changePassword };
}

/** Non-reactive read, for use outside React (interceptors, route loaders). */
export function getAuthSnapshot(): AuthState {
  return useAuthStore.getState();
}
